import {
  Box,
  Typography,
  Paper,
  Button,
  Link,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ScrollableTableContainer from '../components/common/ScrollableTableContainer';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** The four onboarding steps, in order. Only the first has an in-app CTA that
 *  works without an organization slug; the rest are reached from within an org. */
const STEPS = [
  { key: 'context', cta: { to: '/orgs', labelKey: 'help.steps.context.cta' } },
  { key: 'scan' },
  { key: 'attest' },
  { key: 'vpat' },
] as const;

/** Severity rows of the response-target table, worst first.
 *
 *  These restate the AFixt SLA & support policy ratified by the CEO on
 *  2026-10-12 (afixt-security-and-procurement, entity/commercial/
 *  sla-and-support-policy.md §4 and §10), which names this page as one of the
 *  support routes that has to carry them. Change them here only when that
 *  policy is re-ratified -- it is the single source, and a number that differs
 *  from it is the kind of thing a buyer's procurement team notices.
 *
 *  Deliberately absent: any availability or uptime figure. The policy offers
 *  Revenant none until production hosting is defined (REV-421). */
const SEVERITIES = ['s1', 's2', 's3', 's4'] as const;

/**
 * Getting-started / in-product Help (REV-430). Public route /help. Walks a new
 * user from sign-up to their first report and points to deeper resources.
 * Serves P3 exit criterion #4 (signed-up to first scan under 30 minutes).
 */
function HelpPage() {
  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h3" component="h1" gutterBottom>
        {t('help.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('help.subtitle')}
      </Typography>

      <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
        {t('help.stepsHeading')}
      </Typography>

      <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {STEPS.map((step, i) => (
          <Paper key={step.key} component="li" sx={{ p: 3, mb: 2 }}>
            <Typography variant="overline" color="primary">
              {t('help.stepLabel', { number: i + 1 })}
            </Typography>
            <Typography variant="h6" component="h3" gutterBottom>
              {t(`help.steps.${step.key}.title`)}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t(`help.steps.${step.key}.body`)}
            </Typography>
            {'cta' in step && step.cta && (
              <Button component={RouterLink} to={step.cta.to} variant="outlined">
                {t(step.cta.labelKey)}
              </Button>
            )}
          </Paper>
        ))}
      </Box>

      <Typography variant="h5" component="h2" sx={{ mt: 5, mb: 2 }}>
        {t('help.more.heading')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {t('help.more.ci.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('help.more.ci.body')}
            </Typography>
            <Link href="https://github.com/AFixt/a11y-action#hosted-mode-publish-to-the-platform">
              {t('help.more.ci.title')}
            </Link>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {t('help.more.migrate.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('help.more.migrate.body')}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {t('help.more.billing.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('help.more.billing.body')}
            </Typography>
            <Link component={RouterLink} to="/settings/billing">
              {t('help.more.billing.title')}
            </Link>
          </Paper>
        </Grid>
      </Grid>

      {/* REV-1127: what the AI features send, whether it trains models, and how
          to turn them off. Every claim is checked against the code
          (fixRecommendationService, criterionNarrativeService, aiProxyService)
          and the provider's terms as read on 2026-10-26. Subject to the legal
          review REV-1120 owes before the first customer contract. */}
      <Box component="section" aria-labelledby="help-ai-heading" sx={{ mt: 5 }}>
        <Typography id="help-ai-heading" variant="h5" component="h2" gutterBottom>
          {t('help.ai.heading')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.ai.what')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.ai.sent')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.ai.training')}{' '}
          <Link href="https://www.anthropic.com/legal/commercial-terms">
            {t('help.ai.termsLink')}
          </Link>
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.ai.off')}
        </Typography>
      </Box>

      <Box sx={{ mt: 5 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {t('help.support.heading')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.support.body')}
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
          {t('help.support.hoursHeading')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.support.hours')}
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom>
          {t('help.support.targetsHeading')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('help.support.targetsIntro')}
        </Typography>
        <ScrollableTableContainer
          component={Paper}
          variant="outlined"
          label={t('help.support.targetsCaption')}
          sx={{ maxWidth: 640 }}
        >
          <Table size="small">
            <caption>{t('help.support.targetsCaption')}</caption>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  {t('help.support.severityColumn')}
                </TableCell>
                <TableCell component="th" scope="col">
                  {t('help.support.targetColumn')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SEVERITIES.map((key) => (
                <TableRow key={key}>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'normal' }}>
                    {t(`help.support.severities.${key}`)}
                  </TableCell>
                  <TableCell>{t(`help.support.targets.${key}`)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('help.support.targetsCaveat')}
        </Typography>
      </Box>
    </Box>
  );
}

export default HelpPage;
